import styled, { keyframes } from 'styled-components';
import { useEffect, useState } from 'react';
import { Product } from '../types/Product';
import { useTranslation } from 'react-i18next';

const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const ShowcaseContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: linear-gradient(
    to right,
    var(--background-start) 0%,
    var(--background-end) 50%,
    var(--background-start) 100%
  );
  position: relative;
  padding: 2rem 0;
  margin: 2rem 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      var(--primary-color),
      transparent
    );
    opacity: 0.3;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0;
    margin: 1rem 0;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  animation: ${scrollAnimation} ${props => props.theme.isMobile ? '20s' : '40s'} linear infinite;
  &:hover {
    animation-play-state: paused;
  }
`;

const ProductCard = styled.div`
  flex: 0 0 280px;
  margin: 0 1rem;
  background: var(--surface-color);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    flex: 0 0 240px;
    margin: 0 0.5rem;
  }

  &:hover {
    transform: translateY(-10px);
    border-color: var(--primary-color);
    box-shadow: 
      0 5px 15px var(--primary-glow),
      0 0 30px var(--secondary-glow);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const ProductInfo = styled.div`
  padding: 1rem;
`;

const ProductName = styled.h3`
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const ProductCategory = styled.p`
  color: var(--text-light);
  font-size: 0.9rem;
  opacity: 0.8;
`;

export default function ProductShowcase() {
  const [products, setProducts] = useState<Product[]>([]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetch('/data/products.json')
      .then(res => res.json())
      .then(data => setProducts(data.products));
  }, []);

  return (
    <ShowcaseContainer>
      <ScrollContainer>
        {[...products, ...products].map((product, index) => (
          <ProductCard key={`${product.id}-${index}`}>
            <ProductImage src={product.imageUrl} alt={product.name} loading="lazy" />
            <ProductInfo>
              <ProductName>
                {i18n.language === 'en' ? product.englishName : product.name}
              </ProductName>
              <ProductCategory>
                {t(`products.${product.id}.description`, product.description)}
              </ProductCategory>
            </ProductInfo>
          </ProductCard>
        ))}
      </ScrollContainer>
    </ShowcaseContainer>
  );
} 